import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Edit,
  Visibility,
  CheckCircle,
  AccessTime,
  Person,
  Description,
  Fingerprint,
  AttachFile
} from '@mui/icons-material';
import api from '../lib/api';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import SendForSignature from '../components/SendForSignature';
import DocumentManagementPage from './DocumentManagementPage';

const AgreementDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState(0);

  // Fetch agreement details
  const { data: agreement, isLoading } = useQuery({
    queryKey: ['agreement', id],
    queryFn: async () => {
      const res = await api.get(`/agreements/${id}`);
      return res.data.data;
    },
    enabled: !!id
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'EXECUTED':
        return 'success';
      case 'PENDING_SIGNATURES':
        return 'warning';
      case 'DRAFT':
        return 'default';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!agreement) {
    return (
      <Container>
        <Box mt={4}>
          <Alert severity="error">Agreement not found</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={4}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={4}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Agreement {agreement.agreementNumber}
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <Chip
                label={agreement.status}
                color={getStatusColor(agreement.status)}
                icon={agreement.status === 'EXECUTED' ? <CheckCircle /> : <AccessTime />}
              />
              <Typography variant="body2" color="text.secondary">
                Created: {format(new Date(agreement.createdAt), 'MMM dd, yyyy')}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/agreements/${id}/timeline`)}
            >
              Timeline
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<Description />} label="Overview" iconPosition="start" />
            <Tab icon={<AttachFile />} label="Documents" iconPosition="start" />
            <Tab icon={<Fingerprint />} label="Send for Sign" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 1 && <DocumentManagementPage embedded={true} />}
        {activeTab === 2 && <SendForSignature agreementId={id!} agreement={agreement} />}

        {activeTab === 0 && (
          <>
        <Grid container spacing={3}>
          {/* Main Details */}
          <Grid item xs={12} md={8}>
            {/* Agreement Information */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Agreement Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body1">{agreement.type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Assets
                  </Typography>
                  <Typography variant="body1">{agreement.totalAssets}</Typography>
                </Grid>
                {agreement.startDate && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(agreement.startDate), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                )}
                {agreement.endDate && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(agreement.endDate), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {agreement.customRequirements && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Custom Requirements
                  </Typography>
                  <Typography variant="body1">{agreement.customRequirements}</Typography>
                </Box>
              )}
            </Paper>

            {/* Selected Components */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Selected Trust Components ({agreement.selectedComponents?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {agreement.selectedComponents?.map((sc: any) => (
                  <Grid item xs={12} sm={6} key={sc.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="primary">
                          {sc.component.category}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {sc.component.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {sc.component.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Admin Information */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Administrator
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="center">
                <Person sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body1">
                    {agreement.admin.firstName} {agreement.admin.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {agreement.admin.email}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Clients */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Clients ({agreement.clients?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {agreement.clients?.map((agreementClient: any) => (
                  <ListItem key={agreementClient.id}>
                    <ListItemText
                      primary={`${agreementClient.client.firstName} ${agreementClient.client.lastName}`}
                      secondary={
                        <Box>
                          <Chip
                            label={agreementClient.clientType}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {agreementClient.client.email}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Signatures */}
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Signatures ({agreement.signatures?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {agreement.signatures && agreement.signatures.length > 0 ? (
                <List>
                  {agreement.signatures.map((sig: any) => (
                    <ListItem key={sig.id}>
                      <ListItemText
                        primary={`${sig.signer.firstName} ${sig.signer.lastName}`}
                        secondary={format(new Date(sig.signedAt), 'MMM dd, yyyy HH:mm')}
                      />
                      <CheckCircle color="success" />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No signatures yet</Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
        </>
        )}
      </Box>
    </Container>
  );
};

export default AgreementDetailPage;

